import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { BunkerPhase, BunkerPlayerPublic, BunkerPrivateState, BunkerRoomSummary } from '@shum/shared';
import { QrCode } from '../components/QrCode';
import { ReconnectButton } from '../components/ReconnectButton';
import { BunkerScreens } from '../game/bunker/screens';
import { BunkerStyles } from '../game/bunker/styles';
import { api } from '../lib/api';
import { actionId, useBunkerSocket } from '../lib/bunkerSocket';

type Summary = BunkerRoomSummary & { players: BunkerPlayerPublic[] };

export function BunkerRoom() {
  const { code } = useParams<{ code: string }>();
  const normalizedCode = code?.toUpperCase();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [changingIdentity, setChangingIdentity] = useState(false);
  const { state, send, selectPlayer, recover, releaseIdentity, createSeatInvite, clearError, reconnect } = useBunkerSocket(normalizedCode);
  const privateState = state.privateState;

  useEffect(() => {
    if (!normalizedCode) return;
    setSummary(null);
    setSummaryError(null);
    api.getBunkerRoom(normalizedCode).then((data) => setSummary(data as Summary)).catch((error) => setSummaryError((error as Error).message));
  }, [normalizedCode]);

  const players = privateState?.players ?? state.publicState?.players ?? summary?.players ?? [];
  const phase = privateState?.phase ?? state.publicState?.phase ?? summary?.phase ?? 'LOBBY';
  const round = privateState?.roundNumber ?? state.publicState?.roundNumber ?? summary?.roundNumber ?? 0;
  const capacity = privateState?.capacity ?? state.publicState?.capacity ?? summary?.capacity ?? null;
  const joinUrl = useMemo(() => `${window.location.origin}/b/${summary?.code ?? normalizedCode ?? ''}`, [summary?.code, normalizedCode]);

  useGameTextBridge({ privateState, phase, players, round, capacity });

  if (summaryError) return <BunkerFrame><div className="bk-error"><span className="bk-eyebrow">Комната не найдена</span><h1>Эта дверь в бункер не открывается</h1><p>{summaryError}</p><Link className="bk-cta" to="/play/bunker/create">Создать новую комнату</Link></div></BunkerFrame>;
  if (!summary) return <BunkerFrame><div className="bk-stage"><span className="bk-eyebrow">Подключение</span><h1>Ищем бункер…</h1></div></BunkerFrame>;

  return (
    <BunkerFrame status={state.status} onReconnect={reconnect}>
      {state.error && <div className="bk-alert"><span>{state.error.message}</span><button onClick={clearError} aria-label="Закрыть сообщение">×</button></div>}
      {state.replaced && <div className="bk-alert"><span>Сессия открыта на другом устройстве. Войдите снова или используйте код восстановления.</span><button onClick={clearError} aria-label="Закрыть сообщение">×</button></div>}
      {!privateState && phase === 'LOBBY' && (
        <JoinScreen
          code={summary.code}
          players={players}
          selectPlayer={selectPlayer}
          recover={recover}
          changingIdentity={changingIdentity}
          onSelected={() => setChangingIdentity(false)}
        />
      )}
      {privateState?.phase === 'LOBBY' && changingIdentity && (
        <JoinScreen
          code={summary.code}
          players={players}
          selectPlayer={selectPlayer}
          recover={recover}
          changingIdentity
          onSelected={() => setChangingIdentity(false)}
        />
      )}
      {!privateState && phase !== 'LOBBY' && <SpectatorScreen phase={phase} players={players} scenario={state.publicState?.scenario ?? null} bunker={state.publicState?.bunker ?? null} selectPlayer={selectPlayer} />}
      {privateState?.phase === 'LOBBY' && !changingIdentity && (
        <Lobby
          code={summary.code}
          joinUrl={joinUrl}
          players={players}
          capacity={capacity}
          privateState={privateState}
          send={send}
          recoveryCode={state.session?.recoveryCode ?? null}
          onChangeIdentity={() => { releaseIdentity(); setChangingIdentity(true); }}
        />
      )}
      {privateState && privateState.phase !== 'LOBBY' && (
        <Game
          code={summary.code}
          privateState={privateState}
          send={send}
          phase={phase}
          round={round}
          capacity={capacity}
          seatInvite={state.seatInvite}
          createSeatInvite={createSeatInvite}
        />
      )}
    </BunkerFrame>
  );
}

function BunkerFrame({ children, status = 'open', onReconnect }: { children: React.ReactNode; status?: string; onReconnect?: () => void }) {
  return (
    <div className="bk-root">
      <BunkerStyles />
      {onReconnect && (status === 'open' || status === 'connecting' || status === 'closed') && (
        <ReconnectButton status={status} onReconnect={onReconnect} />
      )}
      <div className="bk-main">
        <header className="bk-top">
          <Link className="bk-brand" to="/"><span className="bk-brand-mark">B</span><span>Бункер</span></Link>
          <div className="bk-top-nav">
            <span className={`bk-connection ${status === 'open' ? 'open' : ''}`}>{status === 'open' ? 'на связи' : 'переподключение'}</span>
            <Link className="bk-label" to="/play/bunker">Как играть</Link>
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}

function JoinScreen({
  code,
  players,
  selectPlayer,
  recover,
  changingIdentity = false,
  onSelected,
}: {
  code: string;
  players: BunkerPlayerPublic[];
  selectPlayer: (playerId: string) => boolean;
  recover: (code: string) => boolean;
  changingIdentity?: boolean;
  onSelected?: () => void;
}) {
  const [recoveryCode, setRecoveryCode] = useState('');
  const select = (playerId: string) => {
    if (selectPlayer(playerId)) onSelected?.();
  };
  return (
    <section className="bk-join">
      <span className="bk-eyebrow">Комната {code} · {changingIdentity ? 'исправление выбора' : 'состав подготовлен'}</span>
      <h1>{changingIdentity ? 'Выберите другое имя' : 'Выберите своё имя'}</h1>
      <p>{changingIdentity ? 'Прежнее место уже свободно и снова доступно для выбора — вашим или другим устройством. Выберите любое свободное имя, чтобы продолжить.' : 'Состав уже подготовлен. Выберите себя — после старта закрытая карточка будет доступна только на этом устройстве.'}</p>
      <div className="bk-join-grid">
        <div className="bk-join-panel">
          <span className="bk-label">Состав комнаты</span>
          <div className="bk-name-grid">
            {players.map((player) => (
              <button
                className="bk-name-btn"
                key={player.playerId}
                data-status={player.status}
                disabled={player.status === 'connected' || player.status === 'disconnected'}
                onClick={() => select(player.playerId)}
              >
                <span className="bk-name-avatar">{player.avatarId}</span>
                <span><strong>{player.name}</strong><small>{player.isCreator ? 'ведущий' : joinStatus(player.status)}</small></span>
                {player.isCreator && <em>★</em>}
              </button>
            ))}
          </div>
          <details className="bk-recovery">
            <summary>Восстановить игру на другом устройстве</summary>
            <form onSubmit={(event) => { event.preventDefault(); recover(recoveryCode.trim().toUpperCase()); }}>
              <input className="bk-input" value={recoveryCode} maxLength={8} placeholder="Код восстановления" onChange={(event) => setRecoveryCode(event.target.value.toUpperCase())} />
              <button className="bk-small-cta" disabled={recoveryCode.length < 8}>Восстановить</button>
            </form>
          </details>
        </div>
        <aside className="bk-panel">
          <span className="bk-eyebrow">Как это работает</span>
          <h2 style={{ fontFamily: 'Oswald', fontSize: 34, margin: '8px 0' }}>{changingIdentity ? 'Пока матч не начался' : 'Сначала состав, потом вход'}</h2>
          <p>{changingIdentity ? 'Когда все готовы, ведущий запускает матч и каждому устройству выдаётся закрытая карточка выбранного игрока.' : 'Имена заранее вносит тот, кто создаёт комнату, а ведущего отмечают отдельно. Поэтому никто не сможет добавить себя в матч после подготовки, а карточка останется привязана к выбранному игроку.'}</p>
        </aside>
      </div>
    </section>
  );
}

function Lobby({
  code,
  joinUrl,
  players,
  capacity,
  privateState,
  send,
  recoveryCode,
  onChangeIdentity,
}: {
  code: string;
  joinUrl: string;
  players: BunkerPlayerPublic[];
  capacity: number | null;
  privateState: BunkerPrivateState;
  send: ReturnType<typeof useBunkerSocket>['send'];
  recoveryCode: string | null;
  onChangeIdentity: () => void;
}) {
  const readyCount = players.filter((player) => player.isReady).length;
  const canStart = players.length >= 4 && players.every((player) => player.status === 'connected' && player.isReady);
  return (
    <section className="bk-lobby">
      <div className="bk-lobby-grid">
        <div className="bk-qr-wrap">
          <span className="bk-eyebrow">Лобби · код <b className="bk-code">{code}</b></span>
          <h1>Позовите выживших</h1>
          <p>Покажите QR-код или отправьте ссылку. В игру войдут только те, кто готов обсуждать и голосовать.</p>
          <QrCode value={joinUrl} size={200} />
          <div className="bk-join-url">{joinUrl}</div>
          <Link className="bk-link-btn" to={`/b/${code}/screen`} target="_blank" rel="noreferrer">Открыть общий экран</Link>
          {recoveryCode && <div><span className="bk-eyebrow">Ваш код восстановления</span><div className="bk-recovery-code">{recoveryCode}</div></div>}
        </div>
        <div className="bk-lobby-panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
            <span className="bk-eyebrow">Игроки · {players.length}/24</span>
            <span className="bk-label">готовы {readyCount}/{players.length}</span>
          </div>
          <div className="bk-lobby-list">
            {players.map((player) => (
              <div className="bk-lobby-person" key={player.playerId}>
                <span>{player.avatarId}</span>
                <strong>{player.name} {player.isCreator && '★'}</strong>
                <small className={player.isReady ? 'ready' : ''}>{player.isReady ? 'готов' : joinStatus(player.status)}</small>
              </div>
            ))}
          </div>
          <p>{capacity ? `В бункере подготовлено ${capacity} мест.` : 'Число мест будет рассчитано при старте: половина игроков, округлённая вниз.'}</p>
          <div className="bk-host-actions">
            <button className="bk-cta" onClick={() => send({ type: 'bunker.ready', ready: !privateState.isReady, actionId: actionId() })}>{privateState.isReady ? 'Я не готов' : 'Я готов'}</button>
            <button className="bk-link-btn" type="button" onClick={onChangeIdentity}>Выбрать другое имя</button>
            {privateState.isCreator && <button className="bk-cta" disabled={!canStart} onClick={() => send({ type: 'bunker.start', actionId: actionId() })}>{canStart ? 'Запустить матч →' : 'Ждём всех готовых'}</button>}
          </div>
          {!privateState.isCreator && <p>Начать матч может отмеченный ведущий.</p>}
        </div>
      </div>
    </section>
  );
}

function joinStatus(status: BunkerPlayerPublic['status']): string {
  if (status === 'connected') return 'уже в комнате';
  if (status === 'disconnected') return 'нет связи';
  if (status === 'spectator') return 'наблюдатель';
  return 'выбрать себя';
}

function SpectatorScreen({ phase, players, scenario, bunker, selectPlayer }: {
  phase: BunkerPhase;
  players: BunkerPlayerPublic[];
  scenario: import('@shum/shared').BunkerScenarioPublic | null;
  bunker: import('@shum/shared').BunkerInfoPublic | null;
  selectPlayer: (playerId: string) => boolean;
}) {
  const reconnectablePlayers = phase === 'RESULTS'
    ? []
    : players.filter((player) => player.status === 'disconnected' && player.isActive && !player.isEliminated);
  return (
    <section className="bk-stage">
      <span className="bk-eyebrow">Наблюдатель · {phaseText(phase)}</span>
      <h1>Матч уже начался</h1>
      <p>Вы можете следить за открытыми данными, но закрытые карточки и тайное голосование остаются у игроков.</p>
      {reconnectablePlayers.length > 0 && (
        <div className="bk-panel" style={{ width: 'min(100%, 720px)', textAlign: 'left' }}>
          <span className="bk-eyebrow">Возвращение в матч</span>
          <h2 style={{ fontFamily: 'Oswald', fontSize: 34, margin: '8px 0' }}>Потеряли связь?</h2>
          <p>Можно занять только место игрока со статусом «нет связи». После входа откроется только его закрытая карточка.</p>
          <div className="bk-name-grid">
            {reconnectablePlayers.map((player) => (
              <button className="bk-name-btn" key={player.playerId} type="button" onClick={() => selectPlayer(player.playerId)}>
                <span className="bk-name-avatar">{player.avatarId}</span>
                <span><strong>{player.name}</strong><small>вернуться в игру</small></span>
              </button>
            ))}
          </div>
        </div>
      )}
      {scenario && <p><b>{scenario.title}</b> · {scenario.shortDescription}</p>}
      {bunker && <p><b>{bunker.title}</b> · {bunker.area} · {bunker.autonomy}</p>}
      <div style={{ width: 'min(100%, 720px)' }}><PlayerPreview players={players} /></div>
    </section>
  );
}

function Game({
  code,
  privateState,
  send,
  phase,
  round,
  capacity,
  seatInvite,
  createSeatInvite,
}: {
  code: string;
  privateState: BunkerPrivateState;
  send: ReturnType<typeof useBunkerSocket>['send'];
  phase: BunkerPhase;
  round: number;
  capacity: number | null;
  seatInvite: { playerId: string; playerName: string; token: string; expiresAt: string } | null;
  createSeatInvite: (playerId: string) => boolean;
}) {
  const canControl = privateState.isCreator && phase !== 'RESULTS';
  const [showSeatInvite, setShowSeatInvite] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const reconnectablePlayers = privateState.players.filter((player) => player.status === 'disconnected' && player.isActive && !player.isEliminated);
  return (
    <div className="bk-game">
      <div className="bk-game-topbar">
        <div className="bk-phase"><span className="bk-eyebrow">Раунд {round || '—'} · мест {capacity ?? '—'} · активных {privateState.activePlayerCount}</span><strong>{phaseText(phase)}</strong></div>
        {canControl && <div className="bk-game-actions">
          {privateState.paused ? <button className="bk-host-control" onClick={() => send({ type: 'bunker.resume', actionId: actionId() })}>Продолжить</button> : <button className="bk-host-control" onClick={() => send({ type: 'bunker.pause', actionId: actionId() })}>Пауза</button>}
          {!privateState.paused && phase !== 'PLAYER_SPEECH' && <button className="bk-host-control" onClick={() => send({ type: 'bunker.advance', actionId: actionId() })}>Дальше</button>}
          {reconnectablePlayers.length > 0 && <button className="bk-host-control" type="button" onClick={() => setShowSeatInvite((visible) => !visible)}>{showSeatInvite ? 'Скрыть QR' : 'QR для игрока'}</button>}
        </div>}
      </div>
      {showSeatInvite && privateState.isCreator && reconnectablePlayers.length > 0 && (
        <SeatInvitePanel
          code={code}
          players={reconnectablePlayers}
          seatInvite={seatInvite}
          createSeatInvite={createSeatInvite}
          onClose={() => setShowSeatInvite(false)}
        />
      )}
      <BunkerScreens privateState={privateState} send={send} />
      {privateState.isCreator && (
        <section className="bk-log">
          <label className="bk-log-toggle"><input type="checkbox" checked={showLog} onChange={(event) => setShowLog(event.target.checked)} />Показать журнал матча</label>
          {showLog && (
            <div className="bk-log-list">{privateState.logs.slice().reverse().map((entry) => <div className="bk-log-entry" key={entry.id}><time>{new Date(entry.at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</time><span>{entry.text}</span></div>)}</div>
          )}
        </section>
      )}
    </div>
  );
}

function SeatInvitePanel({
  code,
  players,
  seatInvite,
  createSeatInvite,
  onClose,
}: {
  code: string;
  players: BunkerPlayerPublic[];
  seatInvite: { playerId: string; playerName: string; token: string; expiresAt: string } | null;
  createSeatInvite: (playerId: string) => boolean;
  onClose: () => void;
}) {
  const [requestedPlayerId, setRequestedPlayerId] = useState<string | null>(null);
  const invite = seatInvite?.playerId === requestedPlayerId ? seatInvite : null;
  const inviteUrl = invite ? seatInviteUrl(code, invite.token) : null;
  return (
    <section className="bk-panel" style={{ marginBottom: 24 }}>
      <div className="bk-section-head inline">
        <div><span className="bk-eyebrow">Возвращение игрока</span><h2>QR для конкретного места</h2></div>
        <button className="bk-link-btn" type="button" onClick={onClose}>Закрыть</button>
      </div>
      <p>Выберите отключившегося игрока. QR действует 30 минут и может вернуть только это место. Карточка не показывается ведущему — сервер отправит её только подключившемуся игроку.</p>
      <div className="bk-name-grid">
        {players.map((player) => (
          <button
            className="bk-name-btn"
            data-status={requestedPlayerId === player.playerId ? 'selected' : undefined}
            key={player.playerId}
            type="button"
            onClick={() => {
              if (createSeatInvite(player.playerId)) setRequestedPlayerId(player.playerId);
            }}
          >
            <span className="bk-name-avatar">{player.avatarId}</span>
            <span><strong>{player.name}</strong><small>создать QR для возврата</small></span>
          </button>
        ))}
      </div>
      {requestedPlayerId && !invite && <p className="bk-label" style={{ marginTop: 16 }}>Готовим одноразовый QR-код…</p>}
      {inviteUrl && invite && (
        <div className="bk-qr-wrap" style={{ marginTop: 20 }}>
          <QrCode value={inviteUrl} size={200} />
          <strong>Место: {invite.playerName}</strong>
          <span className="bk-label">Действует до {new Date(invite.expiresAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      )}
    </section>
  );
}

function seatInviteUrl(code: string, token: string): string {
  const url = new URL(`/bunker/${encodeURIComponent(code)}`, window.location.origin);
  url.searchParams.set('seatInvite', token);
  return url.toString();
}

function PlayerPreview({ players }: { players: BunkerPlayerPublic[] }) {
  return <div className="bk-player-wall">{players.map((player) => <article key={player.playerId} className={`bk-public-player ${player.isEliminated ? 'eliminated' : ''}`}><div className="bk-player-name"><span>{player.avatarId}</span><strong>{player.name}</strong></div><div className="bk-public-fields">{player.revealedFields.length ? player.revealedFields.map((field) => <span key={field.type}><b>{field.label}</b> {field.value}</span>) : <span className="unknown">характеристики скрыты</span>}</div></article>)}</div>;
}

function phaseText(phase: BunkerPhase): string {
  const labels: Record<BunkerPhase, string> = {
    LOBBY: 'Лобби', INTRO: 'Катастрофа', CHARACTER_REVIEW: 'Знакомство с персонажем', ROUND_START: 'Новый раунд', REVEAL_SELECTION: 'Выбор характеристики', PLAYER_REVEAL: 'Раскрытие', PLAYER_SPEECH: 'Выступление', GENERAL_DISCUSSION: 'Обсуждение', ACTION_WINDOW: 'Специальные карты', VOTING: 'Голосование', VOTE_RESULTS: 'Результаты', TIE_DISCUSSION: 'Ничья', REVOTE: 'Переголосование', ELIMINATION: 'Исключение', RESULTS: 'Итоги', PAUSED: 'Пауза',
  };
  return labels[phase];
}

function useGameTextBridge({ privateState, phase, players, round, capacity }: { privateState: BunkerPrivateState | null; phase: BunkerPhase; players: BunkerPlayerPublic[]; round: number; capacity: number | null }) {
  useEffect(() => {
    const target = window as Window & { render_game_to_text?: () => string; advanceTime?: (ms: number) => void };
    target.render_game_to_text = () => JSON.stringify({
      game: 'bunker',
      phase,
      round,
      capacity,
      viewer: privateState ? { playerId: privateState.viewerPlayerId, screen: privateState.screen, isActive: privateState.isActive, canSelectReveal: privateState.canSelectReveal } : null,
      currentTurn: privateState ? { playerId: privateState.currentPlayerId, name: privateState.currentPlayerName, index: privateState.currentTurnIndex, total: privateState.currentTurnTotal } : null,
      players: players.map((player) => ({ name: player.name, active: player.isActive, eliminated: player.isEliminated, revealed: player.revealedFields.map((field) => field.type) })),
      timerEndsAt: privateState?.endsAt ?? null,
      coordinates: 'DOM game; no canvas coordinate system. Actions are buttons and forms.',
    });
    target.advanceTime = () => {
      // The game timer is server-authoritative. Tests may use this hook as a deterministic UI refresh point.
      window.dispatchEvent(new Event('bunker-test-tick'));
    };
    return () => {
      delete target.render_game_to_text;
      delete target.advanceTime;
    };
  }, [capacity, phase, players, privateState, round]);
}
